import { DatePicker } from 'antd';
import { FormikHelpers } from 'formik';
import { Moment } from 'moment';
import { LabeledField } from '../../../../components/form-fields/labeled-field';
import { Section } from '../../../../components/form-fields/section';
import type { PromotionForm } from '../../../../stores/promotion';
import style from './style.module.scss';

interface ScheduleSectionProps {
    values: PromotionForm;
    setFieldValue: FormikHelpers<PromotionForm>['setFieldValue'];
    checkIfHasError: (path: string) => boolean;
}

export const ScheduleSection = ({
    values,
    setFieldValue,
    checkIfHasError,
}: ScheduleSectionProps) => {
    return (
        <Section title="Schedule">
            <div className={style.schedule_grid}>
                <LabeledField
                    label="Start date"
                    helperText="The date when the promotion becomes visible in the app (as upcoming or active, depending on Activation Ready Date)."
                    hasError={checkIfHasError('startDate')}
                >
                    <DatePicker
                        allowClear={false}
                        format="YYYY-MM-DD hh:mm A"
                        showTime={{ format: 'HH:mm', minuteStep: 15 }}
                        value={values.startDate}
                        onChange={(date: Moment | null) =>
                            setFieldValue('startDate', date)
                        }
                        disabledDate={() => false}
                    />
                </LabeledField>
                <LabeledField
                    label="Activation Ready Date"
                    helperText="The moment users will be able to use the promotion (subject to availability period, if set)."
                    hasError={checkIfHasError('activationReadyDate')}
                >
                    <DatePicker
                        allowClear={true}
                        format="YYYY-MM-DD hh:mm A"
                        showTime={{ format: 'HH:mm', minuteStep: 15 }}
                        value={values.activationReadyDate ?? null}
                        onChange={(date: Moment | null) =>
                            setFieldValue('activationReadyDate', date)
                        }
                        disabledDate={(date: Moment | null) => {
                            if (!date) return false;
                            if (
                                values.startDate &&
                                date.isBefore(values.startDate, 'minute')
                            )
                                return true;
                            if (
                                values.endDate &&
                                date.isAfter(values.endDate, 'minute')
                            )
                                return true;
                            return false;
                        }}
                    />
                </LabeledField>
                <LabeledField
                    label="Deactivation Display Date"
                    helperText="The moment the promotion will appear expired to users."
                    hasError={checkIfHasError('deactivationDisplayDate')}
                >
                    <DatePicker
                        allowClear={true}
                        format="YYYY-MM-DD hh:mm A"
                        showTime={{ format: 'HH:mm', minuteStep: 15 }}
                        value={values.deactivationDisplayDate ?? null}
                        onChange={(date: Moment | null) =>
                            setFieldValue('deactivationDisplayDate', date)
                        }
                        disabledDate={(date: Moment | null) => {
                            if (!date) return false;
                            if (
                                values.startDate &&
                                date.isBefore(values.startDate, 'minute')
                            )
                                return true;
                            if (
                                values.endDate &&
                                date.isAfter(values.endDate, 'minute')
                            )
                                return true;
                            if (
                                values.activationReadyDate &&
                                date.isBefore(
                                    values.activationReadyDate,
                                    'minute'
                                )
                            )
                                return true;
                            return false;
                        }}
                    />
                </LabeledField>
                <LabeledField
                    label="End date"
                    helperText="The date when the promotion will be automatically removed from the app."
                    hasError={checkIfHasError('endDate')}
                >
                    <DatePicker
                        allowClear={true}
                        format="YYYY-MM-DD hh:mm A"
                        showTime={{ format: 'HH:mm', minuteStep: 15 }}
                        value={values.endDate}
                        onChange={(date: Moment | null) =>
                            setFieldValue('endDate', date)
                        }
                        disabledDate={(date: Moment | null) => {
                            if (!date) return false;
                            if (
                                values.startDate &&
                                date.isBefore(values.startDate, 'minute')
                            )
                                return true;
                            return false;
                        }}
                    />
                </LabeledField>
            </div>
        </Section>
    );
};
